import { useEditor, EditorContent } from '@tiptap/react';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Blockquote from '@tiptap/extension-blockquote';
import Link from '@tiptap/extension-link';
import GalleryNodeView from './GalleryNodeView';
import YoutubeNodeView from './YoutubeNodeView';
import { useEffect, useCallback, useState } from 'react';

function toEmbedUrl(input) {
  const s = input.trim();
  if (s.includes('youtube.com/embed/')) return s;
  const short = s.match(/youtu\.be\/([^?&]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  const watch = s.match(/[?&]v=([^&]+)/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  return s;
}

const Youtube = Node.create({
  name: 'youtube',
  group: 'block',
  atom: true,

  addAttributes() {
    return { src: { default: null } };
  },

  parseHTML() {
    return [{
      tag: 'div.post-video,p.post-video,p.post-centered',
      getAttrs: dom => {
        const iframe = dom.querySelector('iframe[src*="youtube"]');
        if (!iframe) return false;
        return { src: iframe.getAttribute('src') };
      },
    }];
  },

  renderHTML({ node }) {
    return ['div', { class: 'post-video' }, ['iframe', {
      src: node.attrs.src,
      width: '560',
      height: '315',
      title: 'YouTube video player',
      frameborder: '0',
      allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      referrerpolicy: 'strict-origin-when-cross-origin',
      allowfullscreen: 'true',
    }]];
  },

  addNodeView() {
    return ReactNodeViewRenderer(YoutubeNodeView);
  },
});

const CustomBlockquote = Blockquote.extend({
  renderHTML({ HTMLAttributes }) {
    return ['blockquote', mergeAttributes(HTMLAttributes), 0];
  },
});

const Gallery = Node.create({
  name: 'gallery',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      images: {
        default: [],
        parseHTML: element =>
          Array.from(element.querySelectorAll('img')).map(img => ({
            src: img.getAttribute('src') || '',
            alt: img.getAttribute('alt') || '',
          })),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{
      tag: 'div',
      getAttrs: dom =>
        dom.classList.contains('post-gallery') ||
        ['flex', 'items-center', 'justify-center', 'flex-wrap'].every(c => dom.classList.contains(c))
          ? {}
          : false,
    }];
  },

  renderHTML({ node }) {
    const images = node.attrs.images || [];
    return [
      'div',
      { class: 'post-gallery' },
      ...images.map(img => ['img', { src: img.src, alt: img.alt }]),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(GalleryNodeView);
  },
});

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    className={`px-2 py-1 rounded text-sm transition-colors
      ${active ? 'bg-bahyellow text-white' : 'hover:bg-gray-100'}
      ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange }) {
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  const toggleHtmlMode = useCallback(() => {
    setIsHtmlMode(prev => !prev);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [4, 5, 6] }, blockquote: false }),
      CustomBlockquote,
      Link.configure({ openOnClick: false }),
      Gallery,
      Youtube,
    ],
    content: value || '',
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. when loading an existing post)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded focus-within:ring-2 focus-within:ring-bahyellow">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-0.5 p-2 border-b border-gray-200 bg-gray-50 rounded-t">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
          <b>B</b>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
          <i>I</i>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
          <s>S</s>
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} active={editor.isActive('heading', { level: 4 })} title="Heading 4">
          H4
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()} active={editor.isActive('heading', { level: 5 })} title="Heading 5">
          H5
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
          • List
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">
          1. List
        </ToolbarButton>

        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
          ❝
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Add link">
          Link
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive('link')} title="Remove link">
          Unlink
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().insertContent({ type: 'gallery', attrs: { images: [] } }).run()}
          title="Insert image gallery"
        >
          Gallery
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            const url = window.prompt('YouTube URL or embed URL');
            if (!url) return;
            editor.chain().focus().insertContent({ type: 'youtube', attrs: { src: toEmbedUrl(url) } }).run();
          }}
          title="Insert YouTube video"
        >
          YouTube
        </ToolbarButton>

        <span className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          ↩
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          ↪
        </ToolbarButton>

        <span className="flex-1" />

        <ToolbarButton onClick={toggleHtmlMode} active={isHtmlMode} title="Toggle HTML view">
          HTML
        </ToolbarButton>
      </div>

      {isHtmlMode ? (
        <textarea
          className="w-full p-3 min-h-64 font-mono text-xs focus:outline-none resize-y"
          value={value}
          onChange={e => {
            onChange(e.target.value);
            editor.commands.setContent(e.target.value, false);
          }}
        />
      ) : (
        <EditorContent
          editor={editor}
          className="post-content p-3 min-h-64 focus:outline-none prose max-w-none"
        />
      )}
    </div>
  );
}
